import { MenuItem } from '@/types';

// 1. 定义路由元数据类型
interface RouteMeta {
  showBreadcrumb?: boolean;
  showEnterpriseSearch?: boolean;
  keepAlive?: boolean;
}

const WATER_JELLYFISH_REPORT_PLUS_NAME = '\u6c34\u6bcd\u62a5\u544aPlus';

// 2. 定义组件路径常量
const COMPONENTS = {
  BUSINESS: '../views/Search/pages/Business',
  RISK: '../views/Search/pages/Risk',
  ANNUAL_REPORT: '../views/Search/pages/AnnualReport',
  ENTERPRISE_TAX_INVOICE: '../views/Search/pages/EnterpriseTaxInvoice',
  EDUCATION_INFO: '../views/Search/pages/EducationInfo',
  BIG_DATA: '../views/Search/pages/BigData',
  DIY_SEARCH_ABSTRACT: '../views/Search/pages/DiySearchAbstractPage',
  LAWSUIT: '../views/Search/pages/Lawsuit',
  REAL_ESTATE_ASSESSMENT: '../views/Search/pages/RealEstateAssessment',
  HISTORICAL_TRANSACTION: '../views/Search/pages/HistoricalTransaction',
  MARITAL: '../views/Search/pages/Marital',
  VERIFICATION_FOUR: '../views/Search/pages/VerificationFour',
  VERIFICATION_THREE: '../views/Search/pages/VerificationThree',
  REAL_ESTATE: '../views/Search/pages/RealEstate',
  SOCIAL_SECURITY: '../views/Search/pages/SocialSecurity',
  CAR_SEARCH: '../views/Search/pages/CarSearch',
  Verification: '../views/Search/pages/Verification',
} as const;

// 3. 路由创建工具函数
const createRoute = (
  path: string,
  name: string,
  element: string,
  meta: RouteMeta = {},
): MenuItem => ({
  authorityKey: '',
  path,
  name,
  element,
  meta: { showBreadcrumb: true, ...meta },
});

// 4. 创建企业相关路由（business, risk, annual-report）
const createEnterpriseRoutes = (basePath = '/search') => {
  const businessBase = `${basePath}/business/:recordNo`;

  return [
    createRoute(`${businessBase}`, '查工商', COMPONENTS.BUSINESS, {
      showEnterpriseSearch: true,
      keepAlive: true,
    }),
    createRoute(`${businessBase}/risk/:entName`, '扫描风险', COMPONENTS.RISK, {
      showEnterpriseSearch: true,
      showBreadcrumb: true,
      keepAlive: true,
    }),
    createRoute(
      `${businessBase}/annual-report/:entName/:year`,
      '企业年报',
      COMPONENTS.ANNUAL_REPORT,
      { showEnterpriseSearch: true, showBreadcrumb: true, keepAlive: true },
    ),
  ];
};

// 5. 创建diySearch抽象页相关的路由
const createDiySearchAbstractRoutes = () => {
  const basePath = '/search/diySearch/abstract/:initiateOrderNo';

  const routes = [
    // 摘要页本身
    createRoute(basePath, '摘要页', COMPONENTS.DIY_SEARCH_ABSTRACT, { keepAlive: true }),

    // 企业相关（嵌套在abstract下）
    ...createEnterpriseRoutes(basePath).map((route) => ({
      ...route,
      meta: { ...route.meta, showEnterpriseSearch: true, keepAlive: true },
    })),

    // 其他详情页面
    createRoute(`${basePath}/lawsuit/:recordNo?`, '个人诉讼详情', COMPONENTS.LAWSUIT),
    createRoute(`${basePath}/enterprise-lawsuit/:recordNo`, '企业诉讼详情', COMPONENTS.LAWSUIT),
    createRoute(
      `${basePath}/real-estate-assessment/:recordNo?`,
      '房产评估',
      COMPONENTS.REAL_ESTATE_ASSESSMENT,
    ),
    createRoute(
      `${basePath}/historical-transaction/:recordNo?`,
      '历史成交',
      COMPONENTS.HISTORICAL_TRANSACTION,
    ),
    createRoute(`${basePath}/marital/:recordNo?`, '婚姻状况', COMPONENTS.MARITAL),
    createRoute(`${basePath}/verification4/:recordNo?`, '四要素详情', COMPONENTS.VERIFICATION_FOUR),
    createRoute(
      `${basePath}/verification3/:recordNo?`,
      '三要素详情',
      COMPONENTS.VERIFICATION_THREE,
    ),
    createRoute(`${basePath}/real-estate/:recordNo?`, '名下房产', COMPONENTS.REAL_ESTATE),
    createRoute(`${basePath}/social-security/:recordNo?`, '广东社保', COMPONENTS.SOCIAL_SECURITY),
  ];

  // 车辆相关路由（统一处理）
  const carRoutes = [
    { suffix: 'myCar', name: '名下车辆' },
    { suffix: 'ownerVerification', name: '人/企车主核验' },
    { suffix: 'transferNum', name: '过户次数' },
    { suffix: 'drivingPermit', name: '行驶证查询' },
    { suffix: 'carFiveItems', name: '车五项查询' },
    { suffix: 'carInsurance', name: '车辆保险查询' },
    { suffix: 'accidentRecords', name: '出险详情记录' },
    { suffix: 'usedCar', name: '二手车评估' },
  ];

  carRoutes.forEach(({ suffix, name }) => {
    routes.push(createRoute(`${basePath}/${suffix}/:recordNoData?`, name, COMPONENTS.CAR_SEARCH));
  });

  return routes;
};

// 6. 创建普通搜索路由（非diySearch）
const createRegularSearchRoutes = () => {
  const routes = [
    // 企业相关
    ...createEnterpriseRoutes(),

    // 其他独立搜索项
    createRoute(
      '/search/enterprise-taxInvoice/:recordNo?',
      WATER_JELLYFISH_REPORT_PLUS_NAME,
      COMPONENTS.ENTERPRISE_TAX_INVOICE,
    ),
    createRoute('/search/education/:recordNo?', '学历信息', COMPONENTS.EDUCATION_INFO),
    createRoute('/search/bigdata/:recordNo?', '大数据', COMPONENTS.BIG_DATA),

    // 实名核验
    createRoute('/search/verification', '实名核验', COMPONENTS.Verification),
    // 查车辆
    createRoute('/search/carSearch', '查车辆', COMPONENTS.CAR_SEARCH),
  ];

  return routes;
};

// 7. 组合所有路由
const routes: MenuItem[] = [...createRegularSearchRoutes(), ...createDiySearchAbstractRoutes()];

export default routes;
